import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Star, Search, Check, MapPin } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../api';
import { Price } from '../context/CurrencyContext';
import { TravelBookingDialog } from '../components/TravelBookingDialog';

const today = new Date().toISOString().slice(0, 10);

const HotelCard = ({ o, onBook }) => (
  <Card className="border-0 shadow-md hover:shadow-xl transition-shadow overflow-hidden" data-testid={`hotel-offer-${o.offer_id}`}>
    <CardContent className="p-0 grid grid-cols-1 md:grid-cols-12">
      <img src={o.image} alt={o.hotel_name} className="md:col-span-4 h-48 md:h-full w-full object-cover" />
      <div className="md:col-span-5 p-5">
        <div className="flex items-center gap-1 mb-1">{[...Array(o.stars)].map((_, i) => <Star key={i} className="w-4 h-4 fill-yellow-400 text-yellow-400" />)}</div>
        <h3 className="text-xl font-bold text-gray-900">{o.hotel_name}</h3>
        <p className="text-sm text-gray-500 flex items-center mb-3"><MapPin className="w-3 h-3 mr-1" />{o.city} · {o.room_type}</p>
        <div className="flex flex-wrap gap-2 mb-3">{o.amenities.map((a) => <span key={a} className="text-xs bg-teal-50 text-teal-700 px-2 py-0.5 rounded-full flex items-center"><Check className="w-3 h-3 mr-1" />{a}</span>)}</div>
        <p className="text-sm"><span className="bg-teal-600 text-white font-bold px-2 py-0.5 rounded">{o.rating}</span> <span className="text-gray-500">{o.reviews.toLocaleString()} reviews</span>{o.free_cancellation && <span className="ml-3 text-green-600 font-medium">Free cancellation</span>}</p>
      </div>
      <div className="md:col-span-3 p-5 bg-gray-50 flex flex-col justify-center text-right">
        <p className="text-xs text-gray-500">{o.nights} night{o.nights > 1 ? 's' : ''} · {o.rooms} room{o.rooms > 1 ? 's' : ''} · {o.guests} guests</p>
        <Price inr={o.total_price} className="text-2xl font-bold text-teal-700" />
        <p className="text-xs text-gray-500 mb-3"><Price inr={o.price_per_night} /> / night</p>
        <Button data-testid={`hotel-book-${o.offer_id}`} onClick={() => onBook(o)} className="bg-gradient-to-r from-teal-500 to-cyan-600 text-white">Reserve</Button>
      </div>
    </CardContent>
  </Card>
);

const Hotels = ({ user, authChecked }) => {
  const navigate = useNavigate();
  const [cities, setCities] = useState([]);
  const [q, setQ] = useState({ city: 'Kerala', checkin: '', checkout: '', guests: 2 });
  const [offers, setOffers] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);

  useEffect(() => { api.get('/travel/hotel-cities').then((r) => setCities(r.data)).catch(() => {}); }, []);

  const search = async (e) => {
    e?.preventDefault();
    if (!q.checkin || !q.checkout) { toast.error('Pick check-in and check-out dates'); return; }
    if (q.checkout <= q.checkin) { toast.error('Check-out must be after check-in'); return; }
    setLoading(true);
    try {
      const res = await api.get('/travel/hotels', { params: q });
      setOffers(res.data.offers);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Search failed');
    } finally { setLoading(false); }
  };

  const book = (o) => {
    if (!authChecked) return;
    if (!user) { toast.info('Login to reserve hotels'); navigate('/login'); return; }
    setSelected(o);
  };

  return (
    <div className="min-h-screen bg-gray-50 pt-16" data-testid="hotels-page">
      <div className="bg-gradient-to-r from-teal-600 to-cyan-600 text-white py-14">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-4xl sm:text-5xl font-bold mb-2">Find Hotels</h1>
          <p className="text-teal-50 text-lg">Hand-picked stays in every TripSoul destination</p>
          <span className="inline-block mt-3 text-xs bg-white/20 rounded-full px-3 py-1" data-testid="sandbox-badge">Sandbox rates — live hotel inventory connects once provider keys are added</span>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-8">
        <Card className="border-0 shadow-xl">
          <CardContent className="p-5">
            <form onSubmit={search} className="grid grid-cols-1 md:grid-cols-12 gap-4 items-end" data-testid="hotel-search-form">
              <div className="md:col-span-3"><Label className="mb-1 block text-xs">City</Label>
                <Select value={q.city} onValueChange={(v) => setQ((s) => ({ ...s, city: v }))}>
                  <SelectTrigger data-testid="hotel-city" className="border-2 h-11"><SelectValue /></SelectTrigger>
                  <SelectContent>{cities.map((c) => <SelectItem key={c} value={c} data-testid={`hotel-city-option-${c.replace(/\s+/g, '-').toLowerCase()}`}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="md:col-span-3"><Label className="mb-1 block text-xs">Check-in</Label><Input data-testid="hotel-checkin" type="date" min={today} value={q.checkin} onChange={(e) => setQ((s) => ({ ...s, checkin: e.target.value }))} className="border-2 h-11" /></div>
              <div className="md:col-span-3"><Label className="mb-1 block text-xs">Check-out</Label><Input data-testid="hotel-checkout" type="date" min={q.checkin || today} value={q.checkout} onChange={(e) => setQ((s) => ({ ...s, checkout: e.target.value }))} className="border-2 h-11" /></div>
              <div className="md:col-span-1"><Label className="mb-1 block text-xs">Guests</Label><Input data-testid="hotel-guests" type="number" min={1} max={10} value={q.guests} onChange={(e) => setQ((s) => ({ ...s, guests: Number(e.target.value) || 1 }))} className="border-2 h-11" /></div>
              <Button data-testid="hotel-search-btn" type="submit" disabled={loading} className="md:col-span-2 h-11 bg-gradient-to-r from-teal-500 to-cyan-600 text-white"><Search className="w-4 h-4 mr-2" />{loading ? 'Searching...' : 'Search'}</Button>
            </form>
          </CardContent>
        </Card>

        <div className="py-10 space-y-5" data-testid="hotel-results">
          {offers === null ? (
            <p className="text-center text-gray-500 py-10">Choose a city and dates to see available stays.</p>
          ) : offers.length === 0 ? (
            <p className="text-center text-gray-500 py-10" data-testid="hotels-empty">No hotels found.</p>
          ) : (
            <>
              <p className="text-sm text-gray-500" data-testid="hotels-count">{offers.length} stays in {q.city} · {q.checkin} → {q.checkout}</p>
              {offers.map((o) => <HotelCard key={o.offer_id} o={o} onBook={book} />)}
            </>
          )}
        </div>
      </div>

      {selected && <TravelBookingDialog open onOpenChange={(v) => !v && setSelected(null)} type="hotel" offer={selected} count={1} user={user} />}
    </div>
  );
};

export default Hotels;
